import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faLock } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import CheckoutForm from '../components/checkout/CheckoutForm';
import CartItem from '../components/cart/CartItem';
import { useCart } from '../context/CartContext';
import PageHero from '../components/layout/PageHero';
import { Container, Section } from '../styles/components';

const PageContainer = styled.div`
  padding-bottom: ${theme.spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.fonts.heading};
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.darkGray};
  text-decoration: none;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const CheckoutContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const OrderSummary = styled.div`
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.small};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    order: -1;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.lightGray};
`;

const OrderItems = styled.div`
  margin-bottom: ${theme.spacing.lg};
  max-height: 300px;
  overflow-y: auto;
`;

const OrderTotals = styled.div`
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.lightGray};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
  font-size: 1rem;
  
  &.final-total {
    font-size: 1.25rem;
    font-weight: bold;
    margin-top: ${theme.spacing.md};
    padding-top: ${theme.spacing.md};
    border-top: 1px solid ${theme.colors.lightGray};
  }
`;

const SecureCheckout = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
  color: ${theme.colors.darkGray};
  font-size: 0.875rem;
  
  svg {
    color: ${theme.colors.success};
  }
`;

const EmptyCheckout = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} 0;
  
  h2 {
    margin-bottom: ${theme.spacing.md};
  }
  
  p {
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.darkGray};
  }
`;

const CheckoutPage: React.FC = () => {
  const { items, totalItems, totalPrice } = useCart();
  const navigate = useNavigate();
  
  // Redirect to cart if cart is empty
  useEffect(() => {
    if (items.length === 0) {
      const timer = setTimeout(() => {
        navigate('/cart');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [items, navigate]);
  
  const shippingPrice = totalPrice >= 100 ? 0 : 10;
  const finalTotal = totalPrice + shippingPrice;
  
  if (items.length === 0) {
    return (
      <PageContainer>
        <PageHero 
          title="Checkout"
          subtitle="Complete your purchase"
          height="30vh"
        />
        <Section>
          <Container>
            <EmptyCheckout>
              <h2>Your Cart is Empty</h2>
              <p>You need to add items to your cart before checking out.</p>
              <p>Redirecting to your cart...</p>
              <BackLink to="/cart">
                <FontAwesomeIcon icon={faArrowLeft} /> Go to Cart
              </BackLink>
            </EmptyCheckout>
          </Container>
        </Section>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <PageHero 
        title="Secure Checkout"
        subtitle="Complete your purchase securely"
        height="30vh"
      />
      <Section>
        <Container>
          <BackLink to="/cart">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Cart
          </BackLink>
          
          <CheckoutContainer>
            <CheckoutForm />
            
            <OrderSummary>
              <SectionTitle>Order Summary</SectionTitle>
              
              <OrderItems>
                {items.map(item => (
                  <CartItem key={item.id} item={item} />
                ))}
              </OrderItems>
              
              <OrderTotals>
                <TotalRow>
                  <span>Items ({totalItems}):</span>
                  <span>${totalPrice.toFixed(2)}</span>
                </TotalRow>
                <TotalRow>
                  <span>Shipping:</span>
                  <span>{shippingPrice === 0 ? 'Free' : `$${shippingPrice.toFixed(2)}`}</span>
                </TotalRow>
                <TotalRow className="final-total">
                  <span>Total:</span>
                  <span>${finalTotal.toFixed(2)}</span>
                </TotalRow>
              </OrderTotals>
              
              <SecureCheckout>
                <FontAwesomeIcon icon={faLock} />
                <span>Secure Checkout - Your information is protected</span>
              </SecureCheckout>
            </OrderSummary>
          </CheckoutContainer>
        </Container>
      </Section>
    </PageContainer>
  );
};

export default CheckoutPage; 